import { Alert, AlertIcon, Button, Flex, Link, Text, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import * as React from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";

// Custom components
import Card from "components/card/Card";
import ListTable, { LIST_MUTED_COLOR, ListHeader, ListRow } from "components/list/ListTable";
import { usePagination } from "components/list/TablePagination";
import ModalForm from "components/modal/modalForm";
import { warningColor } from "hooks/useForfaitUsage";
import { numberToHour } from "utils";
import SiteDetail from "views/admin/maintenance/components/SiteDetail";
import { EN_LIGNE_LABELS, ETAT_LIGNE_LABELS, SAUVEGARDE_ETAT_LABELS } from "views/admin/maintenance/libelles";
import {
  derniereCollecte,
  enLigneDuSite,
  EtatLigne,
  etatDeLaLigne,
  prioriteDuSite,
  RANG_LIGNE,
  ResultatEnLigne,
  ResultatSauvegarde,
  sauvegardeDuSite,
  SuiviSite,
  suiviDesSites,
  tropAncien,
} from "views/admin/maintenance/suivi";

export type MaintenanceSource = {
  sites: SiteType[];
  clients: ClientType[];
  forfaits: ForfaitType[];
  interventions: InterventionType[];
  etats: SiteEtatType[];
};

type Ligne = SiteType & {
  client: string;
  suivi: SuiviSite;
  sauvegardeEtat: ResultatSauvegarde;
  enLigne: ResultatEnLigne;
  etat: EtatLigne;
};

// Ordre de tri des colonnes Sauvegarde et En ligne : le plus grave d'abord
const GRAVITE_AFFICHEE: ResultatSauvegarde["etat"][] = ["echec", "echec_envoi", "aucune", "en_retard", "avertissement", "jamais", "non_collecte", "ok", "autre", "non_prevue"];
const GRAVITE_EN_LIGNE: ResultatEnLigne["etat"][] = ["hors_ligne", "contenu_anormal", "a_verifier", "non_collecte", "en_ligne"];

// Ton d'affichage : rouge pour ce qui est à traiter, orange pour ce qui est à surveiller, neutre sinon
const TON_SAUVEGARDE: Partial<Record<ResultatSauvegarde["etat"], Ton>> = {
  echec: "alerte",
  echec_envoi: "alerte",
  aucune: "alerte",
  en_retard: "alerte",
  avertissement: "attention",
  jamais: "attention",
  autre: "neutre",
  non_prevue: "neutre",
};
const TON_EN_LIGNE: Partial<Record<ResultatEnLigne["etat"], Ton>> = {
  hors_ligne: "alerte",
  contenu_anormal: "alerte",
  a_verifier: "attention",
};
const TON_LIGNE: Partial<Record<EtatLigne, Ton>> = { a_traiter: "alerte", a_surveiller: "attention", partiel: "neutre", non_collecte: "neutre" };
const ORDRE_FILTRES: EtatLigne[] = ["a_traiter", "a_surveiller", "partiel", "a_jour", "non_collecte"];

type Ton = "alerte" | "attention" | "neutre" | "normal";

// Libellé coloré selon ce qu'il demande, avec une précision en petit
function Statut(props: { ton?: Ton; libelle: string; note?: string; titre?: string }) {
  const alerte = useColorModeValue("red.600", "red.300");
  const attention = useColorModeValue("orange.600", "orange.300");
  const couleur = { alerte, attention, neutre: LIST_MUTED_COLOR, normal: undefined }[props.ton ?? "normal"];
  return (
    <>
      <Text as="span" color={couleur} fontWeight={props.ton === "alerte" || props.ton === "attention" ? "700" : undefined} title={props.titre}>
        {props.libelle}
      </Text>
      {props.note && (
        <Text color={LIST_MUTED_COLOR} fontSize="xs" lineHeight="16px" title={props.titre}>
          {props.note}
        </Text>
      )}
    </>
  );
}

// « il y a 12 min », « il y a 3 h », sinon la date
export function ilYa(date: Date, maintenant = new Date()) {
  const minutes = Math.round((maintenant.getTime() - date.getTime()) / 60000);
  if (minutes < 60) return `il y a ${Math.max(minutes, 0)} min`;
  if (minutes < 24 * 60) return `il y a ${Math.round(minutes / 60)} h`;
  return `le ${moment(date).format("DD/MM/YYYY à HH:mm")}`;
}

const columnHelper = createColumnHelper<Ligne>();
const jour = (date?: Date) => (date ? moment(date).format("DD/MM/YYYY") : "");

// Une donnée pas encore collectée, avec en petit ce qui a été noté sur une intervention
function NonCollecte(props: { note?: string }) {
  return (
    <>
      <Text as="span" color={LIST_MUTED_COLOR}>
        Non collecté
      </Text>
      {props.note && (
        <Text color={LIST_MUTED_COLOR} fontSize="xs" lineHeight="16px" title="Noté sur une intervention, non vérifié">
          {props.note}
        </Text>
      )}
    </>
  );
}

// Tous les forfaits actifs du site : un temps restant par forfait horaire, et les annuels
function TempsRestant(props: { suivi: SuiviSite }) {
  const { forfaits } = props.suivi;
  const shade = useColorModeValue("600", "300");
  if (!forfaits.length) {
    return (
      <Text as="span" color={LIST_MUTED_COLOR}>
        Aucun
      </Text>
    );
  }
  return (
    <>
      {forfaits.map(({ forfait, restant }, index) => {
        if (restant !== undefined) {
          return (
            <Text key={forfait.id} fontWeight="700" color={`${warningColor(restant)}.${shade}`} sx={{ fontVariantNumeric: "tabular-nums" }}>
              {numberToHour(restant)}
            </Text>
          );
        }
        return index === 0 ? (
          <Text key={forfait.id}>Annuel</Text>
        ) : (
          <Text key={forfait.id} color={LIST_MUTED_COLOR} fontSize="xs" lineHeight="16px">
            + Annuel
          </Text>
        );
      })}
    </>
  );
}

export default function MaintenanceTable(props: { source: MaintenanceSource }) {
  const { clients, forfaits, interventions, etats } = props.source;
  const [sites, setSites] = React.useState(props.source.sites);
  // Le tri n'est pas gardé : la page s'ouvre toujours sur ce qui est à faire en premier
  const [sorting, setSorting] = React.useState<SortingState>([{ id: "etat", desc: false }]);
  const [filtre, setFiltre] = React.useState<EtatLigne | "">("");
  const [idOuvert, setIdOuvert] = React.useState<string | null>(null);
  const formulaire = useDisclosure();

  const data = React.useMemo(() => {
    const names = new Map(clients.map((client) => [client.id, client.raison_sociale]));
    const suivis = suiviDesSites(sites, forfaits, interventions);
    const parDomaine = new Map(etats.map((etat) => [etat.domaine, etat]));
    const maintenant = new Date();
    return sites
      .filter((site) => site.cms === "wordpress" && site.actif)
      .map((site) => {
        const sauvegardeEtat = sauvegardeDuSite(site, parDomaine.get(site.domaine), maintenant);
        const enLigne = enLigneDuSite(parDomaine.get(site.domaine), maintenant);
        return {
          ...site,
          client: names.get(site.idclient) ?? "",
          suivi: suivis.get(site.id),
          sauvegardeEtat,
          enLigne,
          etat: etatDeLaLigne(sauvegardeEtat, enLigne),
        };
      });
  }, [sites, clients, forfaits, interventions, etats]);
  const comptes = React.useMemo(() => {
    const c = new Map<EtatLigne, number>();
    data.forEach((ligne) => c.set(ligne.etat, (c.get(ligne.etat) || 0) + 1));
    return c;
  }, [data]);
  const affichees = React.useMemo(() => (filtre ? data.filter((ligne) => ligne.etat === filtre) : data), [data, filtre]);
  const collecte = derniereCollecte(etats);
  const etatsParDomaine = React.useMemo(() => new Map(etats.map((etat) => [etat.domaine, etat])), [etats]);

  // Colonnes créées une seule fois : de nouvelles fonctions `cell` à chaque rendu feraient remonter toutes les cellules
  const columns = React.useMemo(
    () => [
      columnHelper.accessor("nom", {
        id: "site",
        header: "Site / client",
        sortingFn: (a, b) => a.original.nom.localeCompare(b.original.nom, "fr", { sensitivity: "base" }),
        cell: (info) => (
          <>
            <Link as="button" fontWeight="700" textAlign="left" onClick={() => setIdOuvert(info.row.original.id)}>
              {info.getValue()}
            </Link>
            <Text color={LIST_MUTED_COLOR} fontSize="xs" lineHeight="16px">
              {info.row.original.client}
            </Text>
          </>
        ),
      }),
      columnHelper.accessor("etat", {
        id: "etat",
        header: "État",
        meta: { width: "120px" },
        // À traiter d'abord, puis à surveiller… et, à état égal, les mises à jour notées les plus anciennes
        sortingFn: (a, b) =>
          RANG_LIGNE[a.original.etat] - RANG_LIGNE[b.original.etat] || prioriteDuSite(a.original.suivi) - prioriteDuSite(b.original.suivi),
        cell: (info) => <Statut ton={TON_LIGNE[info.getValue()]} libelle={ETAT_LIGNE_LABELS[info.getValue()]} />,
      }),
      columnHelper.accessor((row) => prioriteDuSite(row.suivi), {
        id: "mises_a_jour",
        header: "Mises à jour",
        meta: { width: "170px" },
        cell: (info) => {
          const { suivi } = info.row.original;
          const note = suivi.derniersPlugins ? `notées le ${jour(suivi.derniersPlugins)}` : suivi.forfait?.type === 1 ? "jamais notées" : undefined;
          return <NonCollecte note={note} />;
        },
      }),
      columnHelper.accessor((row) => GRAVITE_AFFICHEE.indexOf(row.sauvegardeEtat.etat), {
        id: "sauvegarde",
        header: "Sauvegarde",
        meta: { width: "170px" },
        cell: (info) => {
          const { sauvegardeEtat, suivi } = info.row.original;
          // Pas de donnée collectée : ce qui a été noté sur une intervention, sans vérification
          if (sauvegardeEtat.etat === "non_collecte") {
            return <NonCollecte note={suivi.derniereSauvegarde ? `notée le ${jour(suivi.derniereSauvegarde)}` : undefined} />;
          }
          const note =
            sauvegardeEtat.etat === "en_retard"
              ? sauvegardeEtat.date
                ? `dernier succès le ${jour(sauvegardeEtat.date)}`
                : "aucun succès"
              : sauvegardeEtat.date
              ? `le ${jour(sauvegardeEtat.date)}`
              : undefined;
          return (
            <Statut
              ton={TON_SAUVEGARDE[sauvegardeEtat.etat]}
              libelle={SAUVEGARDE_ETAT_LABELS[sauvegardeEtat.etat]}
              note={note}
              titre={sauvegardeEtat.message || undefined}
            />
          );
        },
      }),
      columnHelper.accessor((row) => GRAVITE_EN_LIGNE.indexOf(row.enLigne.etat), {
        id: "en_ligne",
        header: "En ligne",
        meta: { width: "130px" },
        cell: (info) => {
          const { enLigne } = info.row.original;
          if (enLigne.etat === "non_collecte") {
            return <NonCollecte />;
          }
          const note = enLigne.depuis ? ilYa(enLigne.depuis).replace(/^il y a /, "depuis ").replace(/^le /, "depuis le ") : enLigne.message;
          return <Statut ton={TON_EN_LIGNE[enLigne.etat]} libelle={EN_LIGNE_LABELS[enLigne.etat]} note={note} titre={enLigne.message} />;
        },
      }),
      columnHelper.accessor((row) => row.suivi.restant, {
        id: "forfait",
        header: "Forfait",
        meta: { width: "90px" },
        sortUndefined: "last",
        cell: (info) => <TempsRestant suivi={info.row.original.suivi} />,
      }),
      columnHelper.accessor((row) => row.suivi.derniereIntervention?.getTime(), {
        id: "derniere_intervention",
        header: "Dern. interv.",
        meta: { width: "125px" },
        sortUndefined: "last",
        sortDescFirst: true,
        cell: (info) =>
          info.row.original.suivi.derniereIntervention ? (
            <Text as="span" whiteSpace="nowrap">
              {jour(info.row.original.suivi.derniereIntervention)}
            </Text>
          ) : (
            <Text as="span" color={LIST_MUTED_COLOR}>
              —
            </Text>
          ),
      }),
    ],
    []
  );

  const [pagination, setPagination] = usePagination(affichees.length);
  const table = useReactTable({
    data: affichees,
    columns,
    state: { sorting, pagination },
    onSortingChange: (updater) => {
      setSorting(updater);
      table.setPageIndex(0);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
  });

  // Lien « Voir la maintenance » depuis la page Sites
  const location = useLocation();
  const history = useHistory();
  const idDemande = new URLSearchParams(location.search).get("site");
  React.useEffect(() => {
    if (idDemande) {
      setIdOuvert(idDemande);
      history.replace(location.pathname);
    }
  }, [idDemande, history, location.pathname]);

  const ouvert = data.find((ligne) => ligne.id === idOuvert) ?? null;
  return (
    <Card flexDirection="column" w="100%" px="0px" pt="16px" pb="8px">
      <ListHeader title={`Maintenance WordPress (${data.length})`}>
        <Text fontSize="sm" color={LIST_MUTED_COLOR}>
          {collecte ? `Collecté ${ilYa(collecte)}` : "Rien n'est encore collecté"}
        </Text>
      </ListHeader>
      {tropAncien(collecte ?? null, new Date()) && (
        <Alert status="warning" mx="25px" w="auto" mb="12px" borderRadius="md">
          <AlertIcon />
          {collecte
            ? `Dernière collecte ${ilYa(collecte)} : n8n ne publie peut-être plus. Sauvegardes et disponibilité s'affichent « Non collecté ».`
            : "n8n n'a encore rien publié : sauvegardes et disponibilité s'affichent « Non collecté »."}
        </Alert>
      )}
      <Flex px="25px" mb="12px" gap="8px" wrap="wrap" align="center">
        {ORDRE_FILTRES.filter((etat) => comptes.get(etat)).map((etat) => (
          <Button
            key={etat}
            size="xs"
            borderRadius="full"
            variant={filtre === etat ? "solid" : "outline"}
            colorScheme={etat === "a_traiter" ? "red" : etat === "a_surveiller" ? "orange" : "gray"}
            aria-pressed={filtre === etat}
            onClick={() => {
              setFiltre(filtre === etat ? "" : etat);
              table.setPageIndex(0);
            }}
          >
            {ETAT_LIGNE_LABELS[etat]} {comptes.get(etat)}
          </Button>
        ))}
        <Text fontSize="xs" color={LIST_MUTED_COLOR}>
          Les mises à jour ne sont pas encore collectées : aucune ligne n'est « À jour ». Les dates « notée(s) le » viennent des
          interventions, sans vérification.
        </Text>
      </Flex>
      <ListTable
        table={table}
        renderRow={(row) => <ListRow key={row.id} row={row} muted={!row.original.suivi.forfait} />}
      />
      <SiteDetail
        site={ouvert}
        etat={ouvert ? etatsParDomaine.get(ouvert.domaine) : undefined}
        forfaits={forfaits}
        interventions={interventions}
        onClose={() => setIdOuvert(null)}
        onModifier={formulaire.onOpen}
      />
      <ModalForm<SiteType>
        isOpen={formulaire.isOpen}
        onClose={formulaire.onClose}
        newData={setSites}
        updateData={ouvert ? [ouvert] : null}
        form="site"
      />
    </Card>
  );
}
